import React, { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  listenToGuestReservationRequests,
  listenToListingAvailability,
  ShortstayRequest,
} from "../../../../redux/shortstayReservationSlice";
import { listenToShortstayData } from "../../../../redux/shortstaysSlice";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import CircularProgress from "@mui/material/CircularProgress";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { motion } from "framer-motion";

import {
  Calendar, MapPin, Clock, Check, X, ChevronDown, ChevronUp, Edit2, AlertTriangle,
  MessageCircle,
} from "lucide-react";
import { useCurrency } from "@cosmoola/react-cosmoola-js";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../../../../firebase";
import { makeThreadId } from "../../../../redux/iMessageSlice";

/* ── tokens ── */
const T = {
  bg:          "#F1EFE8",
  surface:     "#ffffff",
  border:      "#D3D1C7",
  border2:     "#B4B2A9",
  text:        "#2C2C2A",
  textSoft:    "#5F5E5A",
  muted:       "#888780",
  gold:        "#BA7517",
  goldLight:   "#FAEEDA",
  goldBorder:  "#FAC775",
  green:       "#3B6D11",
  greenLight:  "#EAF3DE",
  greenBorder: "#C0DD97",
  red:         "#C0392B",
  redLight:    "#FDEEEC",
  redBorder:   "#F5B7B1",
  sans:        "'Montserrat', sans-serif",
  serif:       "'Cormorant Garamond', serif",
};

const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500&family=Montserrat:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; }
`;

/* ── helpers ── */
const parseISODate = (iso: string): Date => {
  if (!iso) return new Date();
  const clean = iso.split("T")[0];
  const [y, m, d] = clean.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatDate = (iso: string) =>
  parseISODate(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });

const diffNights = (a: string, b: string) =>
  Math.round((parseISODate(b).getTime() - parseISODate(a).getTime()) / 86400000);

const badge: React.CSSProperties = {
  display: "inline-flex", alignItems: "center",
  padding: "3px 9px", borderRadius: 20,
  fontSize: 10, fontWeight: 600, letterSpacing: "0.04em", whiteSpace: "nowrap",
};

const StatusBadge = ({ req }: { req: ShortstayRequest }) => {
  if (req.isApproved)
    return <span style={{ ...badge, background: T.greenLight, color: T.green, border: `1px solid ${T.greenBorder}` }}>Approved</span>;
  if (req.isRejected)
    return <span style={{ ...badge, background: T.redLight, color: T.red, border: `1px solid ${T.redBorder}` }}>Declined</span>;
  return <span style={{ ...badge, background: T.goldLight, color: T.gold, border: `1px solid ${T.goldBorder}` }}>Pending</span>;
};

/* ════════════════════════════════════════════════════════════ */
const GuestShortRequests: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();

  const user       = useSelector((s: RootState) => s.auth.user);
  const userId     = user?.userId || user?.uid || null;
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
const authUser = useSelector((s: RootState) => s.auth.user);

  const requests   = useSelector((s: RootState) => s.shortstayReservations.guestReservationRequests);
  const listings   = useSelector((s: RootState) => s.shortstays.listings);
  const credits    = useSelector((s: RootState) => s.payouts.credits);

  const {
    totalCharged, guestsFee, baseStayCost, discountedStayCost, creditsUsed,
  } = useSelector((s: RootState) => s.shortstayServiceFees);

  const listingAvailability = useSelector((s: RootState) => s.shortstayReservations.listingAvailability);

const { convertRaw, symbol } = useCurrency();

  const [expanded,        setExpanded]        = useState<string | null>(null);
  const [fetchedListings, setFetchedListings]  = useState<string[]>([]);
  const [tab,             setTab]             = useState<"pending" | "approved" | "declined">("pending");
  const [loadingId,       setLoadingId]       = useState<string | null>(null);
  const [openPayId,       setOpenPayId]       = useState<string | null>(null);
  const [payError,        setPayError]        = useState("");

  // requestId → true (available) | false (unavailable)
  const [availabilityMap, setAvailabilityMap] = useState<Record<string, boolean>>({});

  /* ── fetch guest requests ── */
  useEffect(() => {
    if (authLoaded && userId) {
      dispatch(listenToGuestReservationRequests(userId));
    }
  }, [authLoaded, userId, dispatch]);

  /* ── fetch listing data for each request ── */
  useEffect(() => {
    requests.forEach((req) => {
      if (!fetchedListings.includes(req.listingId)) {
        dispatch(listenToShortstayData(req.listingId));
        setFetchedListings((p) => [...p, req.listingId]);
      }
    });
  }, [requests, fetchedListings, dispatch]);

  /* ── real-time availability per approved request ── */
  useEffect(() => {
    const approvedRequests = requests.filter(
      (r) => r.isApproved && r.checkIn && r.checkOut
    );
    if (!approvedRequests.length) return;

    const unsubs: (() => void)[] = [];

    approvedRequests.forEach((req) => {
      const selectedStart = new Date(`${req.checkIn}T12:00:00Z`).getTime();
      const selectedEnd   = new Date(`${req.checkOut}T12:00:00Z`).getTime();

      const q = query(
        collection(db, "shortstayReservations"),
        where("listingId", "==", req.listingId)
      );

      const unsub = onSnapshot(q, (snapshot) => {
        let conflict = false;
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const isActive =
            data.isConfirmed && !data.isCancelled && !data.isRefunded;
          if (!isActive) return;
          const existingStart = new Date(data.checkIn).getTime();
          const existingEnd   = new Date(data.checkOut).getTime();
          if (selectedStart < existingEnd && selectedEnd > existingStart) {
            conflict = true;
          }
        });
        setAvailabilityMap((prev) => ({
          ...prev,
          [req.requestId]: !conflict,
        }));
      });

      unsubs.push(unsub);
    });

    return () => unsubs.forEach((u) => u());
  }, [requests]);

  /* ── compute fees when approved card is expanded ── */
useEffect(() => {
  const approvedRequests = requests.filter(
    (r) => r.isApproved && r.checkIn && r.checkOut
  );
  if (!approvedRequests.length) return;

  const unsubs: (() => void)[] = [];

  approvedRequests.forEach((req) => {
    const unsub = dispatch(
      listenToListingAvailability(req.listingId, req.checkIn, req.checkOut)
    );
    // map the result from Redux state instead of local state
    if (typeof unsub === "function") unsubs.push(unsub);
  });

  return () => unsubs.forEach((u) => u());
}, [requests, dispatch]);

  const getListing = (listingId: string) =>
    listings.find((l: any) => l.listingId === listingId);

  /* ── pay handler ── */
  const handlePay = async (req: ShortstayRequest) => {
    const isAvail = listingAvailability?.available ?? true;
    if (!isAvail || loadingId) return;

    setLoadingId(req.requestId);
    setPayError("");
    const listing = getListing(req.listingId);
    const nights  = req.reservedDuration ?? diffNights(req.checkIn, req.checkOut);

    try {
      const res = await axios.post(
        "https://europe-west3-alreluxe.cloudfunctions.net/createCheckoutSession",
        {
          listingId:        req.listingId,
          userId,
          checkIn:          req.checkIn,
          checkOut:         req.checkOut,
          reservedDuration: nights,
          totalHours:       req.totalHours     ?? 0,
          selectedTimes:    req.selectedTimes  ?? [],
          selectedExtras:   req.selectedExtras ?? [],
          adults:           req.adults         ?? 1,
          children:         req.children       ?? 0,
          infants:          req.infants        ?? 0,
          pets:             req.pets           ?? 0,
          discountApplied:  listing?.discounts ?? {},
          creditsAvailable: credits            ?? 0,
          requestId:        req.requestId,
        }
      );

      const data = res.data;
      const directUrl = data?.url ?? data?.data?.url ?? data?.sessionUrl ?? data?.checkoutUrl;

      if (directUrl) {
        window.location.href = directUrl;
        return;
      }
      if (data?.id) {
        window.location.href = `https://checkout.stripe.com/c/pay/${data.id}`;
        return;
      }
      throw new Error(`No checkout URL returned. Server: ${JSON.stringify(data)}`);

    } catch (err: any) {
      console.error("Checkout failed:", err);
      setPayError(err?.message || "Checkout failed");
    } finally {
      setLoadingId(null);
    }
  };

  /* ── filtered lists ── */
  const filtered = requests.filter((r) => {
    if (tab === "pending")  return r.isPending && !r.isApproved && !r.isRejected;
    if (tab === "approved") return r.isApproved;
    if (tab === "declined") return r.isRejected;
    return false;
  });

  const pendingCount  = requests.filter((r) => r.isPending && !r.isApproved && !r.isRejected).length;
  const approvedCount = requests.filter((r) => r.isApproved).length;
  const unavailCount  = requests.filter((r) => r.isApproved && availabilityMap[r.requestId] === false).length;

  /* ════════════════════════════════════════════════════════════ */
  return (
    <div style={{ background: T.bg, minHeight: "100vh", fontFamily: T.sans, color: T.text, padding: "0 0 80px" }}>
      <style>{FONT_CSS}</style>

      {/* ── heading ── */}
      <div style={{ marginBottom: 20 }}>
        <p style={{ fontSize: 10, fontWeight: 500, color: T.muted, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 4 }}>
          My bookings
        </p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ fontSize: 22, fontWeight: 400, fontFamily: T.serif, color: T.text }}>
            Booking requests
          </h2>
          <div style={{ display: "flex", gap: 6 }}>
            {approvedCount > 0 && (
              <span style={{ ...badge, background: T.greenLight, color: T.green, border: `1px solid ${T.greenBorder}`, fontSize: 11 }}>
                {approvedCount} approved
              </span>
            )}
            {unavailCount > 0 && (
              <span style={{ ...badge, background: T.redLight, color: T.red, border: `1px solid ${T.redBorder}`, fontSize: 11 }}>
                {unavailCount} dates taken
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── global unavailability banner ── */}
      {unavailCount > 0 && (
        <div style={{
          marginBottom: 16, padding: "12px 14px", borderRadius: 10,
          background: T.redLight, border: `1px solid ${T.redBorder}`,
          display: "flex", alignItems: "flex-start", gap: 10,
          fontSize: 13, color: T.red,
        }}>
          <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <strong style={{ display: "block", marginBottom: 2 }}>
              {unavailCount} approved request{unavailCount > 1 ? "s have" : " has"} dates that are now fully booked.
            </strong>
            <span style={{ fontSize: 12, color: "#7b1d1d" }}>
              Please view available dates and pick new ones.
            </span>
          </div>
        </div>
      )}

      {/* ── tabs ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {(["pending", "approved", "declined"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: "7px 16px", borderRadius: 20, border: "none",
              fontFamily: T.sans, fontSize: 12, fontWeight: 500, cursor: "pointer",
              transition: "all .15s",
              background: tab === t ? T.text : T.surface,
              color:      tab === t ? T.bg    : T.muted,
              boxShadow:  tab === t ? "none"  : `0 0 0 1px ${T.border}`,
            }}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === "pending" && pendingCount > 0 && (
              <span style={{ marginLeft: 6, background: T.gold, color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: 10 }}>
                {pendingCount}
              </span>
            )}
            {t === "approved" && unavailCount > 0 && (
              <span style={{ marginLeft: 6, background: T.red, color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: 10 }}>
                {unavailCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── pay error ── */}
      {payError && (
        <div style={{
          marginBottom: 16, padding: "12px 14px", borderRadius: 10,
          background: T.redLight, border: `1px solid ${T.redBorder}`,
          fontSize: 13, color: T.red, display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span>{payError}</span>
          <button onClick={() => setPayError("")} style={{ background: "none", border: "none", cursor: "pointer", color: T.red }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* ── empty ── */}
      {filtered.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 20px", color: T.muted }}>
          <Clock size={32} color={T.border2} style={{ marginBottom: 12 }} />
          <p style={{ fontSize: 14 }}>No {tab} requests</p>
        </div>
      )}

      {/* ── cards ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {filtered.map((req) => {
          const listing   = getListing(req.listingId);
          const nights    = req.reservedDuration ?? diffNights(req.checkIn, req.checkOut);
          const isOpen    = expanded === req.requestId;
          const busy      = loadingId === req.requestId;
          const isPayOpen = openPayId === req.requestId;
          const isAvail   = availabilityMap[req.requestId] ?? true;
          const isUnavail = req.isApproved && isAvail === false;

          return (
            <div
              key={req.requestId}
              style={{
                background: T.surface,
                border: isUnavail
                  ? `1.5px solid ${T.redBorder}`
                  : req.isApproved
                  ? `1.5px solid ${T.greenBorder}`
                  : `1px solid ${T.border}`,
                borderRadius: 14, overflow: "hidden",
              }}
            >
              {/* ── unavailability top banner on card ── */}
              {isUnavail && (
                <div style={{
                  background: T.redLight,
                  borderBottom: `1px solid ${T.redBorder}`,
                  padding: "8px 14px",
                  display: "flex", alignItems: "center", gap: 8,
                  fontSize: 11, color: T.red, fontWeight: 600,
                }}>
                  <AlertTriangle size={12} />
                  These dates are now fully booked — action required
                </div>
              )}

              {/* ── header ── */}
              <div
                onClick={() => setExpanded(isOpen ? null : req.requestId)}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", cursor: "pointer" }}
              >
                {/* photo */}
                <div style={{ width: 54, height: 54, borderRadius: 10, flexShrink: 0, background: T.bg, overflow: "hidden", position: "relative" }}>
                  {listing?.photos?.[0]
                    ? <img src={listing.photos[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <MapPin size={18} color={T.muted} />
                      </div>
                  }
                  {isUnavail && (
                    <div style={{
                      position: "absolute", top: 4, right: 4,
                      width: 10, height: 10, borderRadius: "50%",
                      background: T.red, border: "2px solid #fff",
                    }}/>
                  )}
                </div>

                {/* info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" as const }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {listing?.title || "Listing"}
                    </span>
                    <StatusBadge req={req} />
                    {isUnavail && (
                      <span style={{ ...badge, background: T.redLight, color: T.red, border: `1px solid ${T.redBorder}` }}>
                        Dates taken
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" as const }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.muted }}>
                      <Calendar size={11} />
                      <span>{formatDate(req.checkIn)}</span>
                    </div>
                    <span style={{ fontSize: 11, color: T.border2 }}>→</span>
                    <span style={{ fontSize: 11, color: T.muted }}>{formatDate(req.checkOut)}</span>
                    <span style={{ fontSize: 11, color: T.border2 }}>·</span>
                    <span style={{ fontSize: 11, color: T.muted }}>{nights} night{nights !== 1 ? "s" : ""}</span>
                  </div>
                </div>

                <div style={{ color: T.muted, flexShrink: 0 }}>
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </div>
              </div>

              {/* ── expanded body ── */}
              {isOpen && (
                <div style={{ borderTop: `1px solid ${T.border}`, padding: "16px 16px 18px", background: T.bg }}>

                  {/* listing tile */}
                  {listing && (
                    <div
                      onClick={() => navigate(`/roomdetails/${req.listingId}`)}
                      style={{
                        background: T.surface, border: `1px solid ${T.border}`,
                        borderRadius: 10, padding: "10px 12px", marginBottom: 14, cursor: "pointer",
                      }}
                    >
                      <p style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 2 }}>
                        {listing.title}
                      </p>
                      {listing.address && (
                        <p style={{ fontSize: 11, color: T.muted, display: "flex", alignItems: "center", gap: 4 }}>
                          <MapPin size={10} />
                          {[listing.address.street, listing.address.city, listing.address.country].filter(Boolean).join(", ")}
                        </p>
                      )}
                      <p style={{ fontSize: 10, color: T.gold, marginTop: 4, fontWeight: 500 }}>
                        €{listing.rate} / {listing.rateType === "perHour" ? "hr" : "night"}
                      </p>
                    </div>
                  )}

                  {/* detail rows */}
                  {[
                    { label: "Check-in",  val: formatDate(req.checkIn) },
                    { label: "Check-out", val: formatDate(req.checkOut) },
                    { label: "Duration",  val: `${nights} night${nights !== 1 ? "s" : ""}` },
                    {
                      label: "Guests",
                      val: [
                        `${req.adults ?? 1} adult${(req.adults ?? 1) > 1 ? "s" : ""}`,
                        req.children ? `${req.children} child` : "",
                        req.infants  ? `${req.infants} infant` : "",
                        req.pets     ? `${req.pets} pet`       : "",
                      ].filter(Boolean).join(", "),
                    },
                  ].map(({ label, val }) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${T.border}` }}>
                      <span style={{ fontSize: 12, color: T.muted }}>{label}</span>
                      <span style={{ fontSize: 12, fontWeight: 500, color: T.text }}>{val}</span>
                    </div>
                  ))}

                  {/* ══ PENDING ══ */}
                  {req.isPending && !req.isApproved && !req.isRejected && (
                    <>
                      <div style={{
                        marginTop: 14, padding: "12px 14px", borderRadius: 10,
                        background: T.goldLight, border: `1px solid ${T.goldBorder}`,
                        display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 12,
                      }}>
                        <Clock size={14} color={T.gold} style={{ flexShrink: 0, marginTop: 1 }} />
                        <div>
                          <p style={{ fontSize: 12, fontWeight: 600, color: T.gold, marginBottom: 2 }}>Waiting for host approval</p>
                          <p style={{ fontSize: 11, color: "#633806" }}>
                            The host will review your request. You won't be charged until they approve.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/roomdetails/${req.listingId}`)}
                        style={{
                          width: "100%", padding: "9px 0", borderRadius: 8, marginBottom: 10,
                          border: `1px solid ${T.goldBorder}`, background: T.goldLight,
                          color: T.gold, fontSize: 12, fontWeight: 600,
                          cursor: "pointer", fontFamily: T.sans,
                          display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                        }}
                      >
                        <Edit2 size={13} />
                        Edit request dates / guests
                      </button>
                    </>
                  )}

                  {/* ══ DECLINED ══ */}
                  {req.isRejected && (
                    <>
                      <div style={{
                        marginTop: 14, padding: "12px 14px", borderRadius: 10,
                        background: T.redLight, border: `1px solid ${T.redBorder}`,
                        display: "flex", alignItems: "center", gap: 8,
                        fontSize: 12, color: T.red, marginBottom: 12,
                      }}>
                        <X size={14} />
                        The host declined this request.
                      </div>
                      <button
                        onClick={() => navigate("/shortstays")}
                        style={{
                          width: "100%", padding: "9px 0", borderRadius: 8, marginBottom: 10,
                          border: `1px solid ${T.border2}`, background: "transparent",
                          color: T.textSoft, fontSize: 12, fontWeight: 500,
                          cursor: "pointer", fontFamily: T.sans,
                        }}
                      >
                        Browse other listings
                      </button>
                    </>
                  )}

                  {/* ══ APPROVED ══ */}
                  {req.isApproved && (
                    <>
                      {/* ── DATES TAKEN ── */}
                      {isUnavail ? (
                        <>
                          <div style={{
                            marginTop: 14, padding: "14px", borderRadius: 10,
                            background: T.redLight, border: `1px solid ${T.redBorder}`,
                            display: "flex", alignItems: "flex-start", gap: 10,
                            marginBottom: 14,
                          }}>
                            <AlertTriangle size={15} color={T.red} style={{ flexShrink: 0, marginTop: 1 }} />
                            <div>
                              <p style={{ fontSize: 13, fontWeight: 700, color: T.red, marginBottom: 4 }}>
                                Your approved dates are now fully booked
                              </p>
                              <p style={{ fontSize: 12, color: "#7b1d1d", lineHeight: 1.5 }}>
                                Another guest booked these dates before you completed payment.
                                Please go back to the listing and choose new available dates.
                              </p>
                            </div>
                          </div>

                          <button
                            disabled
                            style={{
                              width: "100%", padding: "13px 0", borderRadius: 10,
                              border: "none", background: T.border2,
                              color: "#fff", fontSize: 14, fontWeight: 700,
                              cursor: "not-allowed", fontFamily: T.sans,
                              opacity: 0.6, marginBottom: 10,
                            }}
                          >
                            Payment unavailable — dates taken
                          </button>

                          <button
                            onClick={() => navigate(`/roomdetails/${req.listingId}`)}
                            style={{
                              width: "100%", padding: "12px 0", borderRadius: 10,
                              border: `1px solid ${T.goldBorder}`, background: T.goldLight,
                              color: T.gold, fontSize: 13, fontWeight: 700,
                              cursor: "pointer", fontFamily: T.sans,
                              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                              marginBottom: 10,
                              boxShadow: "0 2px 8px rgba(186,117,23,.2)",
                            }}
                          >
                            <Calendar size={14} />
                            View available dates
                          </button>
                        </>
                      ) : (
                        <>
                          {/* ── AVAILABLE ── */}
                          <div style={{
                            marginTop: 14, padding: "12px 14px", borderRadius: 10,
                            background: T.greenLight, border: `1px solid ${T.greenBorder}`,
                            display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 14,
                          }}>
                            <Check size={14} color={T.green} style={{ flexShrink: 0, marginTop: 1 }} />
                            <div>
                              <p style={{ fontSize: 12, fontWeight: 600, color: T.green, marginBottom: 2 }}>Host approved your request!</p>
                              <p style={{ fontSize: 11, color: "#1b4a06" }}>
                                Complete your booking by paying below.
                              </p>
                            </div>
                          </div>

                          {/* payment breakdown */}
                          <div style={{
                            background: T.surface, border: `2px solid ${T.goldBorder}`,
                            borderRadius: 16, padding: 20, marginBottom: 14,
                          }}>
                            <div
                              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                              onClick={() => setOpenPayId(isPayOpen ? null : req.requestId)}
                            >
                              <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Payment details</span>
                              {isPayOpen
                                ? <ExpandLess style={{ fontSize: 18, color: T.muted }} />
                                : <ExpandMore style={{ fontSize: 18, color: T.muted }} />
                              }
                            </div>

                            <motion.div
                              initial={false}
                              animate={{ opacity: isPayOpen ? 1 : 0, height: isPayOpen ? "auto" : 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              style={{ overflow: "hidden" }}
                            >
                              <div style={{
                                marginTop: 14, padding: 14,
                                background: "repeating-linear-gradient(#fff,#fff 28px,#f7f7f7 29px)",
                                borderRadius: 10,
                              }}>
                                {[
                                  { label: "Base stay cost", val: baseStayCost },
                                  ...(discountedStayCost < baseStayCost
                                    ? [{ label: "Discount", val: -(baseStayCost - discountedStayCost) }]
                                    : []),
                                  { label: "Service fee", val: guestsFee },
                                  ...(creditsUsed > 0 ? [{ label: "Credits", val: -creditsUsed }] : []),
                                ].map(({ label, val }) => (
                                  <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "7px 0", borderBottom: "1px dashed rgba(0,0,0,.08)", color: T.text }}>
                                    <span style={{ color: T.muted }}>{label}</span>
                                    <span style={{ fontWeight: 500 }}>
{val < 0 ? "-" : ""}{symbol}{Number(convertRaw(Math.abs(val), "EUR")).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                ))}
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700, paddingTop: 10, color: T.text }}>
                                  <span>Total you pay</span>
                                  <span style={{ color: T.gold }}>
{symbol}{Number(convertRaw(totalCharged, "EUR")).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                              </div>
                            </motion.div>

                            {/* dates summary */}
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", marginTop: 14, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
                              <div style={{ padding: "10px 12px" }}>
                                <p style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: "0.08em", marginBottom: 2 }}>CHECK-IN</p>
                                <p style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{formatDate(req.checkIn)}</p>
                              </div>
                              <div style={{ padding: "10px 12px", borderLeft: `1px solid ${T.border}` }}>
                                <p style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: "0.08em", marginBottom: 2 }}>CHECK-OUT</p>
                                <p style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{formatDate(req.checkOut)}</p>
                              </div>
                            </div>

                            {/* pay button */}
                      <button
  onClick={() => handlePay(req)}
  disabled={busy}
  style={{
    marginTop: 16, width: "100%", padding: "13px 0",
    borderRadius: 10, border: "none",
    background: busy ? T.border2 : T.gold,
    color: "#fff", fontSize: 14, fontWeight: 700,
    cursor: busy ? "not-allowed" : "pointer",
    fontFamily: T.sans,
    display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
    opacity: busy ? 0.7 : 1,
    boxShadow: busy ? "none" : "0 4px 14px rgba(186,117,23,.35)",
  }}
>
  {busy
    ? <><CircularProgress size={16} thickness={5} style={{ color: "#fff" }} /><span>Processing…</span></>
    : <>Pay now · <strong>{symbol}{Number(convertRaw(totalCharged, "EUR")).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></>
  }
</button>

                            <p style={{ fontSize: 11, color: T.muted, textAlign: "center", marginTop: 8 }}>
                              You'll be redirected to Stripe. Taxes calculated at checkout.
                            </p>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* ── message host (always) ── */}
     {/* ── message host (always) ── */}
<button
  onClick={() => authUser
    ? navigate(`/imessages/${makeThreadId(authUser.userId, req.hostId)}`)
    : navigate("/AuthSwitch")
  }
  style={{
    width: "100%", padding: "9px 0", borderRadius: 8,
    border: `1px solid ${T.border2}`, background: "transparent",
    color: T.textSoft, fontSize: 12, fontWeight: 500,
    cursor: "pointer", fontFamily: T.sans,
    marginTop: 10,
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
  }}
>
  <MessageCircle size={13} />
  Message host
</button>


                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GuestShortRequests;